// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import Button, { ButtonProps } from "@insite/mobius/Button";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import setQuantityConfirmationModal from "@stanion/App/Handlers/QuantityConfirmationModal/QuantityConfirmationHandler";
import { CustomAppState } from "@stanion/App/Store/Reducers";
import React, { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    extendedStyles?: QuantityConfirmationModalStyles;
}

const mapStateToProps = (state: CustomAppState) => {
    const isCartLoading = getCurrentCartState(state).isLoading;
    const { isUpdatingCart } = state.context;
    return {
        isOpen: state.xQuantityConfirmationModal.isOpen,
        productContext: state.xQuantityConfirmationModal.productContext,
        addToCartPopupTimeout: state.xQuantityConfirmationModal.addToCartPopupTimeout,
        showAddToCartConfirmationDialog: state.xQuantityConfirmationModal.showAddToCartConfirmationDialog,
        isAddToCartButtonDisabled: isCartLoading || isUpdatingCart,
        configurationSelection: state.xQuantityConfirmationModal.configurationSelection,
        onAddToCartClick: state.xQuantityConfirmationModal.onAddToCartClick,
        notes: state.xQuantityConfirmationModal.notes,
        subscription: state.xQuantityConfirmationModal.subscription,
        warehouseQty: state.xQuantityConfirmationModal.warehouseQty,
    };
};

const mapDispatchToProps = {
    setQuantityConfirmationModal,
    addToCart: makeHandlerChainAwaitable<Parameters<typeof addToCart>[0], CartLineModel>(addToCart),
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface QuantityConfirmationModalStyles {
    modal?: ModalPresentationProps;
    container?: InjectableCss;
    topSection?: InjectableCss;
    actionsContainer?: InjectableCss;
    addtoCartButton?: ButtonProps;
    cancelButtonStyle?: ButtonProps;
    messageText?: TypographyPresentationProps;
}
export const quantityConfirmationModalStyles: QuantityConfirmationModalStyles = {};

const styles = quantityConfirmationModalStyles;

const QuantityConfirmationModal = ({
    isOpen,
    setQuantityConfirmationModal,
    isAddToCartButtonDisabled,
    showAddToCartConfirmationDialog,
    addToCartPopupTimeout,
    configurationSelection,
    productContext,
    onAddToCartClick,
    notes,
    subscription,
    addToCart,
    warehouseQty,
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    if (!productContext) {
        return null;
    }

    const { product, productInfo } = productContext;
    const { id: productId, allowZeroPricing, quoteRequired } = product;
    const { qtyOrdered, unitOfMeasure, pricing } = productInfo;

    const addToCartClickHandler = async () => {
        onAddToCartClick?.();

        if (!quoteRequired && !allowZeroPricing && pricing && getUnitNetPrice(pricing, 1).price === 0) {
            toasterContext.addToast({ body: siteMessage("Cart_InvalidPrice"), messageType: "danger" });
            return;
        }

        const sectionOptions =
            configurationSelection && Object.values(configurationSelection).length > 0
                ? Object.values(configurationSelection)
                      .filter(sectionOptionId => !!sectionOptionId)
                      .map(sectionOptionId => ({ sectionOptionId }))
                : undefined;
        const cartline = (await addToCart({
            productId,
            qtyOrdered,
            unitOfMeasure,
            notes,
            sectionOptions,
            subscription,
        })) as CartLineModel;

        if (showAddToCartConfirmationDialog) {
            toasterContext.addToast({
                body: <ProductAddedToCartMessage isQtyAdjusted={cartline.isQtyAdjusted} multipleProducts={false} />,
                messageType: "success",
                timeoutLength: cartline.isQtyAdjusted ? 1000 * 60 * 60 * 24 * 365 : addToCartPopupTimeout,
            });
        }
        setQuantityConfirmationModal({ isOpen: false });
    };

    return (
        <Modal
            {...styles.modal}
            isOpen={isOpen}
            headline={translate("Quantity Confirmation")}
            handleClose={() => setQuantityConfirmationModal({ isOpen: false })}
        >
            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.topSection}>
                    {warehouseQty !== undefined && (
                        <Typography {...styles.messageText}>
                            {siteMessage(
                                "Custom_Quantity_Confirmation_Message",
                                warehouseQty.toString(),
                                product.productNumber,
                                (qtyOrdered - warehouseQty).toString(),
                            )}
                        </Typography>
                    )}
                </StyledWrapper>
                <StyledWrapper {...styles.actionsContainer}>
                    <Button
                        onClick={() => setQuantityConfirmationModal({ isOpen: false })}
                        {...styles.cancelButtonStyle}
                    >
                        {translate("Cancel")}
                    </Button>
                    <Button
                        {...styles.addtoCartButton}
                        onClick={addToCartClickHandler}
                        disabled={qtyOrdered <= 0 || isAddToCartButtonDisabled}
                    >
                        {translate("Ok")}
                    </Button>
                </StyledWrapper>
            </StyledWrapper>
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(QuantityConfirmationModal);
